import {
  useLocalStorage as useLocalStorageHook,
  useReadLocalStorage as useReadLocalStorageHook,
} from 'usehooks-ts';
import type { LocalStorageKey } from './types';
import { serializeLocalStorageKey } from './versioned-key';

export type UseLocalStorageParams<T> = LocalStorageKey & { defaultValue: T };

export function useLocalStorage<T>(args: UseLocalStorageParams<T>) {
  const key = serializeLocalStorageKey(args);
  return useLocalStorageHook(key, args.defaultValue);
}

export function useReadLocalStorage<T>(args: UseLocalStorageParams<T>): T {
  const key = serializeLocalStorageKey(args);
  const value = useReadLocalStorageHook<T>(key);
  return value ?? args.defaultValue;
}

export type WriteToLocalStorageParams<TValue> = LocalStorageKey & {
  value: TValue;
};

export function writeToLocalStorage<TValue>(
  args: WriteToLocalStorageParams<TValue>
) {
  const key = serializeLocalStorageKey(args);
  try {
    localStorage.setItem(key, JSON.stringify(args.value));
    window.dispatchEvent(new StorageEvent('local-storage', { key }));
  } catch (error) {
    console.error('Error writing to local storage', error);
  }
}

export type RemoveFromLocalStorageParams = LocalStorageKey;

export function removeFromLocalStorage(args: RemoveFromLocalStorageParams) {
  const key = serializeLocalStorageKey(args);
  localStorage.removeItem(key);
  window.dispatchEvent(new StorageEvent('local-storage', { key }));
}

export type ReadFromLocalStorageParams<TValue> = LocalStorageKey & {
  defaultValue: TValue;
};

export function readFromLocalStorage<TValue>(
  args: ReadFromLocalStorageParams<TValue>
): TValue {
  const key = serializeLocalStorageKey(args);
  const value = localStorage.getItem(key);
  if (!value) {
    return args.defaultValue;
  }

  try {
    return JSON.parse(value) as TValue;
  } catch {
    return args.defaultValue;
  }
}
